import { useEffect, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import { useSnackbar } from "notistack";
import { ConfirmDialog } from "@/admin/shared/ui";
import {
  useUpdateOrganizationUserRolesMutation,
  ORGANIZATION_ROLE_OPTIONS,
  type OrganizationRoleType,
} from "@/admin/entities/organizationUsers";

interface EditOrganizationUserRolesDialogProps {
  open: boolean;
  id: string;
  roles: OrganizationRoleType[];
  onClose: () => void;
}

export const EditOrganizationUserRolesDialog: React.FC<
  EditOrganizationUserRolesDialogProps
> = ({ open, id, roles, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState<OrganizationRoleType[]>(roles);

  // При каждом открытии диалога начинаем с актуальных ролей строки,
  // не с состояния, оставшегося от предыдущего открытия.
  useEffect(() => {
    if (open) {
      setSelected(roles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [updateRoles, { loading, error }] =
    useUpdateOrganizationUserRolesMutation({
      onCompleted: () => {
        enqueueSnackbar("Роли сотрудника обновлены", { variant: "success" });
        onClose();
      },
    });

  const toggleRole = (role: OrganizationRoleType) => {
    setSelected((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    void updateRoles({ variables: { id, roles: selected } });
  };

  return (
    <ConfirmDialog
      open={open}
      onCancel={onClose}
      onConfirm={handleConfirm}
      confirmTitle="Роли сотрудника"
      confirmText="Выберите роли сотрудника в организации."
      loading={loading}
      error={error}
      disabled={selected.length === 0}
    >
      <FormGroup>
        {ORGANIZATION_ROLE_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={() => toggleRole(option.value)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
      {selected.length === 0 && (
        <FormHelperText error>Выберите хотя бы одну роль</FormHelperText>
      )}
    </ConfirmDialog>
  );
};
